/**
 * CompactPlayerRow container.
 *
 * Exists to bind the list-level `onAdd(playerId)` callback into the
 * zero-argument handler the View renders, with a stable identity — the same
 * `AddPlayersRow` precedent that keeps per-item closures out of a list
 * View's `map` (`react-perf`).
 * @module features/player-kanban/components/molecules/AddPlayersRow/CompactPlayerRow
 */

import React, { useCallback } from "react";

import CompactPlayerRowView from "./CompactPlayerRowView";

/** Props for the CompactPlayerRow container. */
export interface CompactPlayerRowContainerProps {
  /** Player id — the add target and the row's test identity. */
  readonly playerId: string;
  /** Display name. */
  readonly name: string;
  /** Pre-composed meta caption. */
  readonly meta: string;
  /** GPR score for the inline info pill; `null` omits the pill. */
  readonly gpr?: string | null;
  /** Whether the player already sits in this pipeline (muted "In list"). */
  readonly isInList?: boolean;
  /** Whether an add for this player is in flight. */
  readonly isAdding?: boolean;
  /** Add a player, by id. Absent hides the action. */
  readonly onAdd?: (playerId: string) => void;
}

/**
 * Render one compact drawer row with a bound add handler.
 * @param root0 - Component props.
 * @param root0.playerId - Player id.
 * @param root0.name - Display name.
 * @param root0.meta - Meta caption.
 * @param root0.gpr - GPR score for the pill.
 * @param root0.isInList - Whether the player is already in this pipeline.
 * @param root0.isAdding - Whether an add is in flight.
 * @param root0.onAdd - List-level add callback.
 */
const CompactPlayerRowContainer = ({
  playerId,
  name,
  meta,
  gpr,
  isInList,
  isAdding,
  onAdd,
}: CompactPlayerRowContainerProps) => {
  const handleAdd = useCallback(
    () => (onAdd === undefined ? undefined : onAdd(playerId)),
    [onAdd, playerId]
  );

  return (
    <CompactPlayerRowView
      playerId={playerId}
      name={name}
      meta={meta}
      gpr={gpr}
      isInList={isInList}
      isAdding={isAdding}
      onAdd={onAdd === undefined ? undefined : handleAdd}
    />
  );
};

CompactPlayerRowContainer.displayName = "CompactPlayerRowContainer";

export default CompactPlayerRowContainer;
